import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { BottomNav } from "@/components/BottomNav";
import { useLanguage } from "@/contexts/LanguageContext";
import { Paywall } from "@/components/Paywall";
import { 
  ArrowLeft, Home, Search, Droplet, LineChart, MessageCircle,
  Send, Loader2, User
} from "lucide-react";
import { toast } from "sonner";

// Lana Avatar Image URL
const LANA_AVATAR_URL = "https://customer-assets.emergentagent.com/job_glucose-insights-2/artifacts/y7q9fk4i_ChatGPT%20Image%20Mar%205%2C%202026%2C%2004_39_46%20AM.png";

// Lana Avatar component with actual image
const LanaAvatar = ({ size = "md" }) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-20 h-20"
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full overflow-hidden flex-shrink-0 shadow-md border-2 border-primary/20`}>
      <img 
        src={LANA_AVATAR_URL} 
        alt="Lana - Virtual Dietitian" 
        className="w-full h-full object-cover"
      />
    </div>
  );
};

// User Avatar component
const UserAvatar = ({ user, size = "sm" }) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-10 h-10"
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full overflow-hidden flex-shrink-0 bg-secondary flex items-center justify-center`}>
      {user?.profile_photo ? (
        <img 
          src={user.profile_photo} 
          alt={user?.name || "User"} 
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-white font-bold text-xs">
          {user?.name?.charAt(0)?.toUpperCase() || <User className="w-4 h-4 text-white" />}
        </span>
      )}
    </div>
  );
};

// Typing indicator dots animation
const TypingDots = () => {
  return (
    <div className="flex items-center gap-1">
      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
      <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
    </div>
  );
};

// Format AI response - remove markdown and render properly
const formatMessage = (text) => {
  if (!text) return null;
  
  // Split by double newlines for paragraphs
  const paragraphs = text.split(/\n\n+/);
  
  return paragraphs.map((para, pIndex) => {
    // Check if it's a bullet list
    const lines = para.split('\n');
    const isBulletList = lines.some(line => /^[\s]*[-•*]\s/.test(line));
    
    if (isBulletList) {
      const items = lines.filter(line => line.trim());
      return (
        <ul key={pIndex} className="space-y-1 my-2">
          {items.map((item, iIndex) => {
            // Remove bullet characters and markdown
            let cleanItem = item.replace(/^[\s]*[-•*]\s*/, '').trim();
            cleanItem = cleanItem.replace(/\*\*/g, '').replace(/\*/g, '');
            return (
              <li key={iIndex} className="flex items-start gap-2">
                <span className="text-primary mt-1.5">•</span>
                <span>{cleanItem}</span>
              </li>
            );
          })}
        </ul>
      );
    }
    
    // Check if it's a header (starts with ** or ##)
    let cleanPara = para.trim();
    const isHeader = cleanPara.startsWith('**') || cleanPara.startsWith('##');
    
    // Remove markdown formatting
    cleanPara = cleanPara.replace(/\*\*/g, '').replace(/##\s*/g, '').replace(/\*/g, '');
    
    if (isHeader) {
      return (
        <p key={pIndex} className="font-semibold text-secondary my-2">
          {cleanPara}
        </p>
      );
    }
    
    return (
      <p key={pIndex} className="my-2">
        {cleanPara}
      </p>
    );
  });
};

const Chat = () => {
  const { token, user, refreshUser } = useAuth();
  const { t, tArray } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(true);
  const [showPaywall, setShowPaywall] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const scrollAreaRef = useRef(null);
  const inputRef = useRef(null);

  // Check usage limit on mount
  useEffect(() => {
    const checkUsage = async () => {
      try {
        const response = await fetch(`${API}/user/can-ask-lana`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        if (response.ok) {
          const data = await response.json();
          setUsageData(data);
        }
      } catch (error) {
        console.error("Usage check error:", error);
      }
    };
    checkUsage();
  }, [token]);

  // Load chat history or create new session
  useEffect(() => {
    const loadOrCreateSession = async () => {
      try {
        // First try to get recent sessions
        const sessionsRes = await fetch(`${API}/chat/sessions`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });

        if (sessionsRes.ok) {
          const sessions = await sessionsRes.json();
          if (sessions.length > 0) {
            // Load the most recent session
            const recentSession = sessions[0];
            setSessionId(recentSession.session_id);
            
            // Load messages from that session
            const messagesRes = await fetch(`${API}/chat/session/${recentSession.session_id}`, {
              headers: { Authorization: `Bearer ${token}` },
              
            });
            
            if (messagesRes.ok) {
              const sessionMessages = await messagesRes.json();
              if (sessionMessages.length > 0) {
                setShowWelcome(false);
                setMessages(sessionMessages.map(msg => ({
                  role: msg.role,
                  content: msg.message,
                })));
              }
            }
          }
        }
      } catch (error) {
        console.error("Load session error:", error);
      }
    };

    loadOrCreateSession();
  }, [token]);

  // Start new conversation
  const startNewConversation = () => {
    setSessionId(null);
    setMessages([]);
    setShowWelcome(true);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) return;

    // Check if user can ask (limit check)
    if (usageData && !usageData.can_ask && usageData.reason !== "premium") {
      setShowPaywall(true);
      return;
    }

    const userMessage = message.trim();
    setMessage("");
    setLoading(true);
    setShowWelcome(false);

    // Add user message immediately
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);

    try {
      const response = await fetch(`${API}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session_id);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.response },
        ]);
        
        // Refresh user data and usage limits
        if (refreshUser) {
          await refreshUser();
        }
        
        // Re-check usage after sending message
        const usageRes = await fetch(`${API}/user/can-ask-lana`, {
          headers: { Authorization: `Bearer ${token}` },
          
        });
        if (usageRes.ok) {
          const newUsage = await usageRes.json();
          setUsageData(newUsage);
        }
      } else {
        toast.error(t('chat.failed_response'));
        setMessages((prev) => prev.slice(0, -1));
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error(t('chat.failed_send'));
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSuggestedQuestion = (question) => {
    setMessage(question);
    inputRef.current?.focus();
  };

  // Get suggested questions from translations
  const suggestedQuestions = tArray('chat.suggested_questions');

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-secondary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <LanaAvatar size="sm" />
              <div>
                <h1 className="font-heading text-lg font-bold text-secondary">Lana</h1>
                <p className="text-xs text-muted-foreground">{t('chat.virtual_dietitian')}</p>
              </div>
            </div>
          </div>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={startNewConversation}
              className="text-primary hover:text-primary/80 text-sm"
              data-testid="new-chat-btn"
            >
              {t('common.new_chat')}
            </Button>
          )}
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-4 pb-44 overflow-hidden">
        <div
          ref={scrollAreaRef}
          className="h-full overflow-y-auto space-y-4 pr-2"
          style={{ maxHeight: 'calc(100vh - 260px)' }}
        >
          {/* Welcome Message */}
          {showWelcome && messages.length === 0 && (
            <div className="space-y-6 py-4">
              <div className="flex flex-col items-center text-center space-y-4">
                <LanaAvatar size="lg" />
                <div className="space-y-2">
                  <h2 className="font-heading text-2xl font-bold text-secondary">
                    {t('chat.welcome_title')}
                  </h2>
                  <p className="text-muted-foreground">
                    {t('chat.welcome_intro')}
                  </p>
                </div>
              </div>
              
              {/* What Lana can help with */}
              <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 max-w-md mx-auto">
                <p className="font-medium text-secondary mb-3">{t('chat.can_help')}</p>
                <ul className="space-y-2 text-muted-foreground">
                  {tArray('chat.help_items').map((item, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              
              {/* Example questions */}
              <div className="text-center space-y-3">
                <p className="text-sm text-muted-foreground">{t('chat.ask_things_like')}</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="px-4 py-2 rounded-full bg-orange-50 text-secondary text-sm hover:bg-orange-100 transition-colors"
                      data-testid={`suggested-question-${index}`}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Messages */}
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {msg.role === "assistant" && <LanaAvatar size="sm" />}
              <div
                className={`max-w-[80%] p-4 rounded-3xl ${
                  msg.role === "user"
                    ? "bg-primary text-white rounded-br-lg"
                    : "bg-white border border-orange-100/50 text-secondary rounded-bl-lg"
                }`}
              >
                <div className="text-sm leading-relaxed">
                  {msg.role === "assistant" ? formatMessage(msg.content) : msg.content}
                </div>
              </div>
              {msg.role === "user" && <UserAvatar user={user} size="sm" />}
            </div>
          ))}
          
          {/* Loading indicator */}
          {loading && (
            <div className="flex gap-3 justify-start">
              <LanaAvatar size="sm" />
              <div className="bg-white border border-orange-100/50 p-4 rounded-3xl rounded-bl-lg">
                <div className="flex items-center gap-3">
                  <TypingDots />
                  <span className="text-sm text-muted-foreground">{t('chat.typing')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Input Area */}
      <div className="fixed bottom-24 left-0 right-0 bg-gradient-to-t from-background via-background to-transparent pt-6 pb-4 px-4">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <Card className="flex items-center gap-2 p-2 rounded-full border border-orange-100/50 bg-white shadow-soft-lift">
            <Input
              ref={inputRef}
              type="text"
              placeholder={t('chat.input_placeholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              className="flex-1 border-0 focus-visible:ring-0 bg-transparent px-4"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={loading || !message.trim()}
              className="rounded-full bg-primary text-white hover:bg-primary/90 w-10 h-10 p-0"
              data-testid="send-message-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </Card>
        </form>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Paywall Modal */}
      <Paywall
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        trigger="lana_limit"
        usageData={usageData}
      />
    </div>
  );
};

export default Chat;
